/**
 * Ticketing vs Cashless on the Attendees list (RP-2477).
 *
 * A profile is "Cashless" when it has a cashless asset (a wristband or a
 * cashless QR: rfid_assets with is_access_only false or null) or when it has
 * no ticket order at all. It stays Cashless when it later buys tickets on the
 * web (QA on RP-2477, 2026-10-01: the Wurstfest Opa profiles and people who
 * registered first and bought tickets later must not show under Ticketing).
 *
 * A profile is "Ticketing" only when it has at least one orders_ticketing row
 * and no cashless asset: it exists because of a ticket checkout. Ticket QR
 * codes are rfid_assets with is_access_only true and do not count.
 *
 * Needs the Hasura array relationships attendees.orders_ticketing and
 * attendees.rfid_assets, and an index on rfid_assets (attendee_id); without
 * the index the filter scans rfid_assets once per attendee.
 */
export type AttendeeTypeFilter = "all" | "ticketing" | "cashless";

export const ATTENDEE_TYPE_FILTER_OPTIONS: { value: AttendeeTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "ticketing", label: "Ticketing" },
  { value: "cashless", label: "Cashless" },
];

export const parseAttendeeTypeFilter = (raw: string | null): AttendeeTypeFilter =>
  raw === "ticketing" || raw === "cashless" ? raw : "all";

/** Hasura bool_exp fragment: attendee has a ticketing order. */
export const HAS_TICKETING_ORDER_WHERE = { orders_ticketing: {} };

/** Hasura bool_exp fragment: attendee has a cashless asset (band or cashless QR, not a ticket). */
export const HAS_CASHLESS_ASSET_WHERE = {
  rfid_assets: { _or: [{ is_access_only: { _eq: false } }, { is_access_only: { _is_null: true } }] },
};

/** Ticketing = a ticket order and no cashless asset. Cashless = everything else. */
export const IS_TICKETING_WHERE = { _and: [HAS_TICKETING_ORDER_WHERE, { _not: HAS_CASHLESS_ASSET_WHERE }] };

export const applyAttendeeTypeFilter = (
  where: Record<string, any>,
  filter: AttendeeTypeFilter
): Record<string, any> => {
  if (filter === "ticketing") return { _and: [where, IS_TICKETING_WHERE] };
  if (filter === "cashless") return { _and: [where, { _not: IS_TICKETING_WHERE }] };
  return where;
};
